<template>
    <div
        :class="[
            'flex h-full w-full min-w-0 flex-col overflow-hidden rounded-2xl p-5 text-white shadow-lg sm:p-6',
            bgColor
        ]"
    >
        <!-- Header -->
        <div class="mb-6">
            <h3 :class="['mb-2 break-words text-xl font-bold leading-tight sm:text-2xl', titleColor || textColor]">
                {{ title }}
            </h3>
            <p :class="['break-words text-sm', textColor]">
                {{ description }}
            </p>
        </div>

        <!-- Features List (gap instead of space-y: the global li margin needs !mb-0, which would cancel space-y) -->
        <ul class="mb-6 flex flex-col gap-2">
            <li
                v-for="(feature, index) in features"
                :key="index"
                :class="['!mb-0 flex items-start gap-2 text-sm', textColor]"
            >
                <span class="mt-0.5 opacity-75">•</span>
                <span class="break-words">{{ feature }}</span>
            </li>
        </ul>

        <!-- Example Cards Preview (hidden on mobile to keep the page short) -->
        <div v-if="exampleCards && exampleCards.length > 0" class="mb-6 hidden space-y-2 sm:block">
            <NuxtLink
                v-for="(card, index) in exampleCards.slice(0, 3)"
                :key="index"
                :to="card.slug && card.linkPrefix ? `${card.linkPrefix}/${card.slug}` : link"
                class="flex w-full min-w-0 items-center gap-3 overflow-hidden rounded-lg bg-white p-2.5 transition-all hover:scale-[1.02] hover:shadow-md dark:bg-surface-card"
            >
                <div class="size-8 shrink-0 overflow-hidden rounded-md bg-gray-100 sm:size-9 dark:bg-surface-subtle">
                    <CThumbnailImage
                        :src="card.image"
                        :alt="card.name || card.title"
                        :type="card.imageType || 'project'"
                        class="size-full"
                    />
                </div>
                <div class="min-w-0 flex-1">
                    <div class="truncate text-sm font-semibold text-gray-900 dark:text-txt-headings">
                        {{ card.name || card.title }}
                    </div>
                    <div
                        v-if="card.field || card.org || card.company || card.provider"
                        class="truncate text-xs text-gray-500 dark:text-txt-body"
                    >
                        {{ card.field || card.org || card.company || card.provider }}
                    </div>
                </div>
            </NuxtLink>
        </div>

        <!-- CTA Button -->
        <NuxtLink
            :to="link"
            :class="[
                'mt-auto inline-flex w-full items-center justify-center gap-2 rounded-lg px-4 py-3 text-sm font-semibold transition-colors sm:text-base',
                buttonClass || 'bg-white text-gray-900 hover:bg-gray-100'
            ]"
        >
            <span>Preskúmať</span>
            <span>→</span>
        </NuxtLink>
    </div>
</template>

<script setup lang="ts">
import CThumbnailImage from '@/components/ui/CThumbnailImage.vue'

interface ExampleCard {
    name?: string
    title?: string
    field?: string
    org?: string
    company?: string
    provider?: string
    type?: string
    image?: string
    imageType?: string
    slug?: string
    linkPrefix?: string
}

interface Props {
    title: string
    description: string
    bgColor: string
    textColor?: string
    titleColor?: string
    buttonClass?: string
    link: string
    features: string[]
    exampleCards?: ExampleCard[]
}

withDefaults(defineProps<Props>(), {
    textColor: 'text-white',
    titleColor: '',
    buttonClass: '',
    exampleCards: () => []
})
</script>
